/**
 * SPEC-qa-list-refresh — pure decision-logic for the Delivery QA list
 * (`pages/qa-list.tsx`, route `/qa/list`).
 *
 * The repo has no jsdom (see vitest.server.config.ts); the established
 * convention is to keep every wiring-critical decision in a pure, imported
 * helper so it is locked by `lib/__tests__/qa-list-helpers.test.ts` and reflects
 * what the page actually does. DOM/visual criteria are covered by the dev-server
 * browser smoke test. The page imports these; it does NOT re-implement them.
 *
 * Mirrors the prescreen-helpers recipe (parse/build tab↔URL) so the two queue
 * surfaces share one shape. `computeMatchState` was moved here out of
 * qa-list.tsx so the Mismatch tile count is unit-testable.
 */

import { normalizeFieldValue } from "@/lib/qa-detail-shared";

// ─────────────────────────────────────────────────────────────────────────────
// Tab ↔ URL (OQ-1: tab state lives in the URL, deep-linkable, default needsqa)
// ─────────────────────────────────────────────────────────────────────────────

export type ListTab = "needsqa" | "inprogress" | "completed" | "skipped";
const VALID_TABS: readonly ListTab[] = ["needsqa", "inprogress", "completed", "skipped"];

/** Read the active tab from the URL search string; unknown/missing → "needsqa". */
export function parseListTab(search: string): ListTab {
  const t = new URLSearchParams(search).get("tab");
  return (VALID_TABS as readonly string[]).includes(t ?? "")
    ? (t as ListTab)
    : "needsqa";
}

/** Build the canonical `?date=…&tab=…` search string for deep-linking a tab. */
export function buildListTabSearch(tab: ListTab, date: string): string {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  params.set("tab", tab);
  return `?${params.toString()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Derived match-state for the Attention column (moved from qa-list.tsx).
//
// Priority (top-down, first match wins):
//   1. mismatch   — any field with both sides present and differing
//   2. no_match   — any field with expected present but actual missing
//   3. first_time — no PDR record (Blue is never absorbed by Green)
//   4. match      — has PDR record AND everything comparable matches
//   5. none       — nothing meaningful to compare (chip suppressed)
//
// Session 14 (DZ-aware unit gating, rules confirmed with Sarah 2026-05-24):
// a blank actual UNIT number is no longer a No Match when the unit was never
// visually obtainable. Only the resident's own door (DZ = DOOR) bears a unit
// number; every other concrete drop zone (front desk, storage, hand-to-hand,
// other entrance, interior porch) is a place the completion photo cannot show
// unit signage, so a blank actual unit there is excused, not an extraction
// failure. The gate keys off the ACTUAL drop zone (what happened). UNK and
// UNSAFE are themselves anomalous → Mismatch regardless of the unit. A unit
// that is present where none was expected, or present at a non-door DZ, is a
// Mismatch (the model surfaced something that shouldn't be there). Street # is
// always required regardless of DZ. This scorer is shared with the qa-detail-v3
// header badge so the List row and the detail page agree on the same delivery.
// ─────────────────────────────────────────────────────────────────────────────

export type MatchState = "mismatch" | "no_match" | "first_time" | "match" | "none";

/** Per-field comparison outcome. `na` = field is not applicable (excused, e.g.
 *  a unit that was never obtainable); `neutral` = nothing to compare. */
export type FieldStatus = "match" | "mismatch" | "no_match" | "na" | "neutral";

// DZ codes that are themselves anomalous regardless of any other field.
const DZ_ANOMALOUS = new Set(["unk", "unsafe"]);

function dzNorm(v: unknown): string {
  return normalizeFieldValue((v ?? "") as string);
}
function dzIsDoor(actDze: unknown): boolean {
  return dzNorm(actDze) === "door";
}
function dzIsH2H(actDze: unknown): boolean {
  return dzNorm(actDze) === "h2h";
}
function dzIsAnomalous(actDze: unknown): boolean {
  return DZ_ANOMALOUS.has(dzNorm(actDze));
}
function valuesEqual(a: unknown, b: unknown): boolean {
  return normalizeFieldValue(a as string) === normalizeFieldValue(b as string);
}

/** Street number — always required; a blank actual against an expected value is
 *  always a No Match (Examples A, E). */
function streetFieldStatus(expStreet: unknown, actStreet: unknown): FieldStatus {
  if (actStreet) {
    if (!expStreet) return "neutral";
    return valuesEqual(expStreet, actStreet) ? "match" : "mismatch";
  }
  return expStreet ? "no_match" : "neutral";
}

/** Unit number — gated on the ACTUAL drop zone (Session 14). */
function unitFieldStatus(expUnit: unknown, actUnit: unknown, actDze: unknown): FieldStatus {
  const doorActual = dzIsDoor(actDze);
  if (actUnit) {
    if (!expUnit) return "mismatch";                                    // Example K — present, none expected
    if (doorActual) return valuesEqual(expUnit, actUnit) ? "match" : "mismatch";
    if (actDze) return "mismatch";                                      // Example F — present at a non-door DZ
    return valuesEqual(expUnit, actUnit) ? "match" : "mismatch";        // DZ unknown — compare values (Example C)
  }
  // actUnit blank
  if (!expUnit) return "neutral";
  return doorActual ? "no_match" : "na";                                // B (door, unread) vs D/E/G/H/I (excused)
}

/** Drop zone — blank actual against an expected value is a No Match (Example C);
 *  UNK/UNSAFE are anomalous → Mismatch; actual H2H is always treated as a match
 *  (hand-to-hand is the most secure handoff, overrides a differing expected). */
function dzFieldStatus(expDze: unknown, actDze: unknown): FieldStatus {
  if (dzIsAnomalous(actDze)) return "mismatch";                         // UNK, UNSAFE
  if (!actDze) return expDze ? "no_match" : "neutral";                  // Example C
  if (dzIsH2H(actDze)) return "match";                                  // H2H override
  if (!expDze) return "neutral";
  return valuesEqual(expDze, actDze) ? "match" : "mismatch";            // Example J
}

export interface FieldComparison {
  expStreet?: unknown; actStreet?: unknown;
  expUnit?: unknown;   actUnit?: unknown;
  expDze?: unknown;    actDze?: unknown;
}

export interface FieldStatuses { street: FieldStatus; unit: FieldStatus; dz: FieldStatus; }

/** Compute the three per-field statuses. Callers pass already-resolved values
 *  (the qa-detail header applies its edit overrides before calling). */
export function computeFieldStatuses(f: FieldComparison): FieldStatuses {
  return {
    street: streetFieldStatus(f.expStreet, f.actStreet),
    unit:   unitFieldStatus(f.expUnit, f.actUnit, f.actDze),
    dz:     dzFieldStatus(f.expDze, f.actDze),
  };
}

/** Collapse the per-field statuses into the single Attention bucket.
 *  Priority: mismatch > no_match > first_time > match > none. */
export function overallMatchState(
  s: FieldStatuses,
  hasPdrReference: boolean,
  anyExpected: boolean,
): MatchState {
  const vals: FieldStatus[] = [s.street, s.unit, s.dz];
  if (vals.includes("mismatch")) return "mismatch";
  if (vals.includes("no_match")) return "no_match";
  if (!hasPdrReference && anyExpected) return "first_time";
  if (vals.includes("match")) return "match";
  return "none";
}

export function computeMatchState(d: any): MatchState {
  // DZ source: the list payload ships `expectedDzeCode` (the canonical
  // DropZoneCode column). `expectedDzePrimary` is kept as a defensive fallback
  // for any caller that still carries it. Before Session 14 this read
  // `expectedDzePrimary` only — which the list endpoint never populates — so DZ
  // silently never contributed to the bucket; switching to `expectedDzeCode`
  // activates it.
  const expDze = d.expectedDzeCode ?? d.expectedDzePrimary;
  const statuses = computeFieldStatuses({
    expStreet: d.expectedStreetNumber, actStreet: d.actualStreetNumber,
    expUnit: d.expectedUnitNumber,     actUnit: d.actualUnitNumber,
    expDze,                            actDze: d.actualDzeCode,
  });
  const anyExpected = !!(d.expectedStreetNumber || d.expectedUnitNumber || expDze);
  const hasPdrReference = d.pdrVerifiedCount != null;
  return overallMatchState(statuses, hasPdrReference, anyExpected);
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3 — stat-tile counts, computed over the full set fetched for the date
// (all rows across all tabs — OQ-4).
// ─────────────────────────────────────────────────────────────────────────────

interface TileCountInput {
  qaDecision?: string | null;
  starlingStatus?: string | null;
  // …plus the expected*/actual*/pdrVerifiedCount fields computeMatchState reads.
  [key: string]: unknown;
}

export function listTileCounts(
  deliveries: TileCountInput[] | null | undefined,
): { mismatch: number; issues: number; waiting: number } {
  const list = deliveries ?? [];

  const mismatch = list.filter(
    (d) =>
      computeMatchState(d) === "mismatch" &&
      d.starlingStatus !== "Skipped" &&
      d.starlingStatus !== "Resolved" &&
      d.starlingStatus !== "Verified",
  ).length;

  // Issues = wrongCount + followUpCount, matching qa-list.tsx's prior tile math.
  // wrongCount and followUpCount are independent counts that are SUMMED, so a row
  // that is both Bad and Needs-Resolution contributes to both (sum = 2). Kept
  // identical to today's behaviour. Named "Issues" (not "Flagged") so "flag"
  // only ever means a validation flag.
  const wrongCount = list.filter((d) => d.qaDecision === "Bad").length;
  const followUpCount = list.filter(
    (d) => d.qaDecision === "Unclear" || d.starlingStatus === "Needs Resolution",
  ).length;
  const issues = wrongCount + followUpCount;

  const waiting = list.filter((d) => d.starlingStatus === "Waiting").length;

  return { mismatch, issues, waiting };
}

// ─────────────────────────────────────────────────────────────────────────────
// Needs-QA filter counter (2026-05-22 decision): hidden when no filter narrows
// the set; reappears as "X of Y" once a driver/type/attention filter is active.
// ─────────────────────────────────────────────────────────────────────────────

export function formatFilterCount(filteredLen: number, totalLen: number): string | null {
  if (filteredLen === totalLen) return null;
  return `${filteredLen} of ${totalLen}`;
}
